// src/context/AuthContext.js (FIXED VERSION - NO ESLINT WARNINGS)
import React, { createContext, useState, useEffect, useCallback } from "react";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Load initial state from localStorage
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem("user");
      if (!saved) return null;
      
      const parsed = JSON.parse(saved);
      
      // Ensure user has required properties
      return {
        username: parsed.username || "User",
        email: parsed.email || "",
        role: parsed.role || "USER",
        id: parsed.id || null
      };
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  });

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("isAuthenticated") === "true" || localStorage.getItem("user") !== null;
  });

  // TOKEN STATE - IMPORTANT FOR API REQUESTS
  const [token, setToken] = useState(() => {
    return localStorage.getItem("authToken") || localStorage.getItem("token") || null;
  });

  // Also store any redirect data that was interrupted
  const [redirectData, setRedirectData] = useState(() => {
    try {
      const saved = sessionStorage.getItem("pendingRedirect");
      if (!saved) return null;
      return JSON.parse(saved);
    } catch (error) {
      console.error("Error parsing redirect data:", error);
      return null;
    }
  });

  // Debug function to check auth state - memoized with useCallback
  const debugAuthState = useCallback(() => {
    console.log("🔍 AuthContext Debug:");
    console.log("User:", user);
    console.log("isAuthenticated:", isAuthenticated);
    console.log("Token exists:", !!token);
    console.log("Token length:", token ? token.length : 0);
    console.log("Token sample:", token ? token.substring(0, 20) + "..." : "No token");
    console.log("LocalStorage user:", localStorage.getItem("user"));
    console.log("LocalStorage authToken:", localStorage.getItem("authToken") ? "Exists" : "Missing");
    console.log("SessionStorage authToken:", sessionStorage.getItem("authToken") ? "Exists" : "Missing");
    console.log("Redirect data:", redirectData);
  }, [user, isAuthenticated, token, redirectData]);

  const login = (userData, authToken) => {
    console.log("🔐 LOGIN CALLED in AuthContext");
    
    // Debug the incoming data
    console.log("=== LOGIN DATA DEBUG ===");
    console.log("userData parameter:", userData);
    console.log("authToken parameter:", authToken ? "Provided" : "Not provided");
    console.log("Type of userData:", typeof userData);
    console.log("Keys in userData:", Object.keys(userData || {}));
    
    // Extract token from response - backend now returns token directly
    let finalToken = authToken || userData?.token;
    
    // Extract user data from the nested structure
    const apiUser = userData?.user || userData;
    
    // Normalize user data to ensure consistent structure
    const normalizedUser = {
      username: apiUser?.username || apiUser?.email?.split('@')[0] || "User",
      email: apiUser?.email || "",
      role: apiUser?.role || "USER",
      id: apiUser?.id || null
    };
    
    console.log("✅ Normalized user data:", normalizedUser);
    console.log("✅ Auth token to store:", finalToken ? `Token exists (${finalToken.length} chars)` : "NO TOKEN FOUND");
    
    // CRITICAL: Store BOTH user and token
    setUser(normalizedUser);
    setToken(finalToken);
    setIsAuthenticated(true);
    
    // Store in localStorage (persistent)
    localStorage.setItem("user", JSON.stringify(normalizedUser));
    localStorage.setItem("authToken", finalToken || "");
    localStorage.setItem("isAuthenticated", "true");
    
    // Also store in sessionStorage for immediate access
    if (finalToken) {
      sessionStorage.setItem("authToken", finalToken);
    }
    
    // Clear any pending redirect data since we're now logged in
    if (redirectData) {
      console.log("📝 Clearing pending redirect data after login");
      setRedirectData(null);
      sessionStorage.removeItem("pendingRedirect");
    }
    
    console.log("✅ Login complete - User and Token stored");
    
    // Log full auth state after login
    debugAuthState();
  };

  const logout = useCallback(() => {
    console.log("🔒 LOGOUT CALLED");
    setUser(null);
    setToken(null); // Clear token too
    setIsAuthenticated(false);
    setRedirectData(null);
    
    // Clear ALL auth storage
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    localStorage.removeItem("token");
    localStorage.removeItem("isAuthenticated");
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("pendingRedirect");
    
    // Clear Authorization header
    if (window.api && window.api.defaults) {
      delete window.api.defaults.headers.common['Authorization'];
    }
    
    console.log("✅ Logout complete - All auth data cleared");
  }, []);

  // Update token (for refresh or new token)
  const updateToken = useCallback((newToken) => {
    console.log("🔄 Updating token");
    setToken(newToken);
    localStorage.setItem("authToken", newToken);
    sessionStorage.setItem("authToken", newToken);
  }, []);

  // Save pending redirect data for interrupted payments
  const saveRedirectData = useCallback((data) => {
    console.log("💾 Saving redirect data:", data);
    setRedirectData(data);
    sessionStorage.setItem("pendingRedirect", JSON.stringify(data));
  }, []);

  // Clear redirect data after it's been processed
  const clearRedirectData = useCallback(() => {
    console.log("🗑️ Clearing redirect data");
    setRedirectData(null);
    sessionStorage.removeItem("pendingRedirect");
  }, []);

  // Check if we have pending redirect (for interrupted payments)
  const hasPendingRedirect = useCallback(() => {
    return !!redirectData;
  }, [redirectData]);

  // Initialize on mount - load token and setup API
  useEffect(() => {
    console.log("🔄 AuthContext mounted");
    
    // Check for token and setup API
    const storedToken = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
    if (storedToken && user) {
      console.log("🔍 Found existing token, setting up API");
      setToken(storedToken);
      
      // Setup API with token
      if (window.api && window.api.defaults) {
        window.api.defaults.headers.common['Authorization'] = `Token ${storedToken}`;
      }
    }
    
    debugAuthState();
  }, [debugAuthState, user]);

  // Update isAuthenticated when user changes
  useEffect(() => {
    const authStatus = !!user && !!token;
    setIsAuthenticated(authStatus);
    
    // If no user but token exists, clear the token
    if (!user && token) {
      console.log("⚠️ User is null but token exists - clearing token");
      setToken(null);
      localStorage.removeItem("authToken");
      sessionStorage.removeItem("authToken");
    }
    
    // If no token but user exists, try to get token from localStorage
    if (user && !token) {
      const storedToken = localStorage.getItem("authToken");
      if (storedToken) {
        console.log("🔍 Found token in localStorage, restoring...");
        setToken(storedToken);
        
        // Setup API with token
        if (window.api && window.api.defaults) {
          window.api.defaults.headers.common['Authorization'] = `Token ${storedToken}`;
        }
      }
    }
  }, [user, token]);

  // Provide context value with memoized functions
  const contextValue = React.useMemo(() => ({
    user, 
    isAuthenticated, 
    token, // CRITICAL: Make token available
    redirectData, // For handling interrupted payments
    login, 
    logout,
    updateToken,
    saveRedirectData,
    clearRedirectData,
    hasPendingRedirect,
    debugAuthState // For debugging
  }), [
    user, 
    isAuthenticated, 
    token, 
    redirectData, 
    login, 
    logout, 
    updateToken, 
    saveRedirectData, 
    clearRedirectData, 
    hasPendingRedirect, 
    debugAuthState
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};